<template>
  <header class="header">
    <div class="header-container" :class="headerContainerClasses">
      <icon-base
        white class="header__menu"
        v-if="!isAuthorizationPage"
        @click.native="toggleSidePanel(!opened)"
      >
        <icon-menu></icon-menu>
      </icon-base>
      <router-link title="Go to Home Page" class="header__logo" to="/">
        <icon-logo />
      </router-link>
      <div class="header__right-col">
        <search-control
          :placeholder="searchControlPlaceholder"
          :useInHeader="true"
          @onResultClick="moveToCurrencyPage"
          @onEnterPress="moveToCurrencyPage"></search-control>
        <template v-if="loginInfo">
          <router-link class="link" to="/settings">
            <div class="header__email">
              {{ loginInfo.decodedData.email | formatEmail }}
            </div>
          </router-link>
          <router-link to="/subscription/tariffs" class="header__upgrade-btn">
            <button-el center white>UPGRADE</button-el>
          </router-link>
        </template>
        <div v-else class="header__login-btns">
          <button
            class="header__login-btn"
            @click="clickOnLoginBtn()"
          >LOGIN</button>
          <span class="header__login-btns-separator">&</span>
          <button
            class="header__login-btn"
            @click="clickOnSignUpBtn()"
          >SIGN UP</button>
        </div>
      </div>
    </div>
  </header>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { formatEmail } from 'src/utils/helpers';
import { IconLogo, IconMenu } from 'Icons';
import loginModalTabs from 'src/models/LoginModalTabs';
import checkMobileVersion from 'src/mixins/checkMobileVersion';

export default {
  mixins: [
    checkMobileVersion,
  ],
  components: {
    IconLogo,
    IconMenu,
  },
  data: () => ({
    loginModalTabs,
  }),
  methods: {
    ...mapActions('settings', ['toggleLoginModal']),
    ...mapActions('tickers', ['updateTickers']),
    ...mapActions('sidePanel', ['toggleSidePanel']),
    moveToCurrencyPage(row) {
      this.$router.push(`/currency/${row.id}`);
    },
    clickOnLoginBtn() {
      const url = '/authorization-page/sign-in';
      if (this.isLandingPage || this.isMobile) {
        this.$router.push(url);
        return;
      }
      if (this.isAuthorizationPage) {
        this.$router.replace(url);
        return;
      }
      this.toggleLoginModal({ val: true, activeTab: loginModalTabs.login });
    },
    clickOnSignUpBtn() {
      const url = '/authorization-page/sign-up';
      if (this.isLandingPage || this.isMobile) {
        this.$router.push(url);
        return;
      }
      if (this.isAuthorizationPage) {
        this.$router.replace(url);
        return;
      }
      this.toggleLoginModal({ val: true, activeTab: loginModalTabs.register });
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },
  computed: {
    ...mapState('settings', ['showLoginModal']),
    ...mapState('sidePanel', ['opened']),
    ...mapState('userData', ['loginInfo']),
    ...mapState('tickers', ['totalRowsCount']),
    ...mapGetters('tickers', ['searchControlPlaceholder']),
    needToLoadTickersCount() {
      return !this.totalRowsCount && !this.$route.meta.dontUpdateHeaderTickersCount;
    },
    isLandingPage() {
      return this.$route.meta.isLandingPage;
    },
    isAuthorizationPage() {
      return this.$route.meta.isAuthorizationPage;
    },
    isAllCurPage() {
      return this.$route.meta.isAllCurPage;
    },
    headerContainerClasses() {
      const classes = [];
      if (!this.loginInfo) {
        classes.push('header-container_unlogged');
      }
      if (this.isAllCurPage) {
        classes.push('header-container_all-cur');
      }
      if (this.$route.meta.isSettingsPage) {
        classes.push('header-container_settings');
      }
      if (this.$route.meta.isTariffsPage) {
        classes.push('header-container_tariffs');
      }
      if (this.$route.meta.isAddAlertPage) {
        classes.push('header-container_add-alert');
      }
      if (this.$route.meta.isSubscriptionManagement) {
        classes.push('header-container_subscr-management');
      }
      return classes;
    },
  },
  filters: {
    formatEmail,
  },
  watch: {
    // Watch for router changing to detect necessary loading tickersTotalCount
    // we need it because header is loaded before router-view
    $route: {
      handler() {
        if (this.needToLoadTickersCount) {
          this.updateTickers();
        }
      },
      deep: true,
    },
  },
};

</script>
<style lang="scss" scoped>
$margin: 30px;

.header-container {
  height: $header-height;
  display: flex;
  justify-content: space-between;
  @include container;
  max-width: 1160px;
  padding-left: 40px;
  padding-right: 39px;
  transition: max-width $transition*0.8 ease, padding-left $transition*0.1 ease $transition*0.1;
  &_all-cur {
    max-width: 964px;
    padding-left: 30px;
    padding-right: 0;
  }
  &_all-cur#{&}_unlogged {
    padding-right: 30px;
  }
  &_settings,
  &_tariffs {
    max-width: 1197px;
    padding-left: 30px;
    padding-right: 0;
  }
  &_add-alert {
    max-width: 1184px;
    padding-left: 21px;
  }
  &_subscr-management {
    max-width: 1184px;
    padding-left: 23px;
  }
}
.header {
  background: $red-color3;
  &__menu {
    display: none;
  }
  &__logo {
    vertical-align: middle;
    align-self: center;
    margin-top: -2px;
  }
  &__email {
    margin-left: $margin;
    @include font(13px, $white-color2, 600);
    max-width: 180px;
  }
  &__right-col {
    display: flex;
    flex: 1;
    justify-content: flex-end;
    align-items: center;
  }
  &__upgrade-btn {
    display: inline-block;
    margin-left: $margin;
    width: 150px;
  }
  &__login-btns {
    margin-left: $margin;
    @include font(13px, $white-color2, 600);
  }
  &__login-btns-separator {
    color: $red-color1;
  }
  &__login-btn {
    cursor: pointer;
    @include removeBtnDefaults;
    @include font(13px, $white-color2, 600);
  }
  &_homepage {
    box-shadow: 10px 10px 20px $black-color9;
    z-index: 2;
  }
}

@media only screen and (max-width : 1280px) {
  .header-container_settings {
    padding-right: 40px;
  }
  .header-container_tariffs {
    padding-right: 19px;
  }
  .header-container_subscr-management .header__logo {
    position: absolute;
    left: 41px;
    margin-top: -1px;
  }
}
@media only screen and (max-width : 1279px) {
  .header-container_add-alert {
    padding-left: 40px;
  }
}
@media only screen and (max-width : 1040px) {
  .header-container_all-cur {
    padding-right: 30px;
  }
}
@media only screen and (max-width : 1024px) {
  .header-container {
    &_all-cur {
      padding-left: 40px;
    }
    &_tariffs {
      max-width: 964px;
      padding-left: 40px;
      padding-right: 30px;
    }
  }
}
@media only screen and (max-width : 900px) {
  .header__upgrade-btn {
    display: none;
  }
}

@include mobileStyles {
  .header__right-col {
    display: none;
  }
  .header-container {
    padding-left: 0;
    padding-right: 0;
    justify-content: center;
    &_subscr-management {
      .header__logo {
        position: static;
        left: auto;
        margin-top: -2px;
      }
    }
  }
  .header {
    position: relative;
  }
  .header__menu {
    display: block;
    position: absolute;
    left: 22px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10000;
  }
}
@media only screen and (max-width: 768px) {
  .header-container_tariffs {
    padding-left: 40px;
  }
}
@media only screen and (max-width: 432px) {

}
</style>



// WEBPACK FOOTER //
// src/components/layout/HeaderEl.vue